/**
 * TicketTriageCard — the AI-triage panel for an in-app support ticket (Slice 3).
 *
 * Renders the backend `ai_triage` block: severity, refined category, likely cause,
 * suggested steps, and the related server errors. Design-law: severity is encoded
 * by hierarchy + a single restrained adverse signal (high only); never brand orange
 * (that's reserved for nav + primary action). Handles queued / failed states.
 */
import React from 'react';
import { Loader2, Sparkles, AlertTriangle } from 'lucide-react';

export interface TriageErrorRef {
  endpoint?: string;
  status?: number | string;
  created_at?: string;
}
export interface AiTriage {
  status?: 'queued' | 'done' | 'failed';
  severity?: 'low' | 'medium' | 'high';
  category?: string;
  likely_cause?: string;
  suggested_steps?: string[];
  related_errors?: TriageErrorRef[];
  reason?: string;
  model?: string;
  generated_at?: string;
}

function SeverityChip({ severity }: { severity?: string }) {
  const s = (severity || 'medium').toLowerCase();
  // Only the adverse end carries a signal hue; medium/low stay hueless and lean
  // on weight + opacity (design-law: minimal color, state via hierarchy first).
  const style =
    s === 'high'
      ? { color: 'var(--color-status-adverse)', borderColor: 'var(--color-status-adverse)' }
      : s === 'medium'
        ? { color: 'var(--color-text-primary)', borderColor: 'var(--color-text-muted)' }
        : { color: 'var(--color-text-muted)', borderColor: 'var(--color-border)' };
  return (
    <span
      className="inline-flex items-center rounded-full border px-2 py-0.5 font-mono text-[10px] uppercase tracking-wide"
      style={style}
    >
      {s}
    </span>
  );
}

const fmtErrDate = (d?: string) => {
  if (!d) return '';
  const t = new Date(d);
  return isNaN(t.getTime()) ? d : t.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

export default function TicketTriageCard({ triage }: { triage: AiTriage }) {
  const status = triage?.status || 'queued';

  if (status === 'queued') {
    return (
      <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--color-text-muted)' }}>
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        AI triage analyzing…
      </div>
    );
  }

  if (status === 'failed') {
    return (
      <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--color-text-dim)' }}>
        <AlertTriangle className="w-3.5 h-3.5" />
        AI triage unavailable{triage?.reason ? ` (${triage.reason})` : ''} — triage never blocks a ticket.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Sparkles className="w-4 h-4" style={{ color: 'var(--color-text-muted)' }} />
        <span className="font-mono text-[10px] uppercase tracking-wide" style={{ color: 'var(--color-text-muted)' }}>
          AI Triage
        </span>
        <SeverityChip severity={triage.severity} />
        {triage.category && (
          <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
            {triage.category}
          </span>
        )}
      </div>

      {triage.likely_cause && (
        <div>
          <div className="font-mono text-[10px] uppercase tracking-wide mb-1" style={{ color: 'var(--color-text-dim)' }}>
            Likely cause
          </div>
          <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>{triage.likely_cause}</p>
        </div>
      )}

      {triage.suggested_steps && triage.suggested_steps.length > 0 && (
        <div>
          <div className="font-mono text-[10px] uppercase tracking-wide mb-1" style={{ color: 'var(--color-text-dim)' }}>
            Suggested steps
          </div>
          <ol className="list-decimal pl-4 space-y-0.5 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
            {triage.suggested_steps.map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </ol>
        </div>
      )}

      {triage.related_errors && triage.related_errors.length > 0 && (
        <div>
          <div className="font-mono text-[10px] uppercase tracking-wide mb-1" style={{ color: 'var(--color-text-dim)' }}>
            Related errors
          </div>
          <ul className="space-y-1">
            {triage.related_errors.map((e, i) => (
              <li key={i} className="flex items-center gap-2 font-mono text-xs" style={{ color: 'var(--color-text-muted)' }}>
                <span
                  className="inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] shrink-0"
                  style={{
                    color: String(e.status).startsWith('5') ? 'var(--color-status-adverse)' : 'var(--color-text-muted)',
                    borderColor: 'var(--color-border)',
                  }}
                >
                  {e.status}
                </span>
                {/* timestamp sits inline right after the endpoint so repeated
                    endpoints read as distinct occurrences, not a render bug (K3). */}
                <span className="shrink-0" style={{ color: 'var(--color-text-dim)' }}>{fmtErrDate(e.created_at)}</span>
                <span className="truncate" title={e.endpoint}>{e.endpoint}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {triage.model && (
        <div
          className="font-mono text-[10px] mt-1 pt-2"
          style={{ color: 'var(--color-text-dim)', borderTop: '1px solid var(--color-border)' }}
        >
          {triage.model}
          {triage.generated_at ? ` · ${fmtErrDate(triage.generated_at)}` : ''}
        </div>
      )}
    </div>
  );
}
